import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa";
import API from "../api/api";

export default function Footer() {
  const [popular, setPopular] = useState([]);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const fetched = useRef(false);

  /* ================= FETCH TRENDING ================= */
  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    API.get("/api/articles/trending")
      .then((res) => {
        if (Array.isArray(res.data)) {
          setPopular(res.data.slice(0, 3));
        }
      })
      .catch(() => { });
  }, []);

  /* ================= NEWSLETTER ================= */
  const handleSubscribe = async () => {
    if (!email) {
      setMessage("Please enter a valid email.");
      return;
    }

    setLoading(true);
    try {
      const res = await API.post("/newsletter/subscribe", { email });
      setMessage(res.data?.message || "Subscribed successfully!");
      setEmail("");
    } catch {
      setMessage("Subscription failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#111] text-gray-300 mt-16">
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-12">

        {/* ===== ABOUT ===== */}
        <div>
          <div className="flex justify-center md:justify-start">
            <img
              src="/logo-blogysports.png"
              alt="BlogySports logo"
              loading="lazy"
              className="
                mb-5
                w-56 sm:w-60 md:w-64
                h-auto
                object-contain
                drop-shadow-[0_0_8px_rgba(255,255,255,0.15)]
              "
            />
          </div>

          <p className="text-sm leading-relaxed text-gray-400">
            <strong>BlogySports</strong> delivers trusted sports news, match
            updates, analysis, and trending stories from cricket, football,
            basketball, and more.
          </p>
        </div>

        {/* ===== POPULAR ARTICLES ===== */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-white">
            Popular Articles
          </h3>

          <div className="space-y-4">
            {popular.map((post) => (
              <Link
                key={post.id}
                to={`/article/${post.slug}`}
                className="flex gap-3 group items-center"
              >
                <img
                  src={post.images || "/logo-blogysports.png"}
                  alt={post.title}
                  loading="lazy"
                  className="w-20 h-14 object-cover rounded"
                />

                <p className="text-sm text-gray-400 group-hover:text-white line-clamp-2">
                  {post.title}
                </p>
              </Link>
            ))}
          </div>
        </div>

        {/* ===== NEWSLETTER + SOCIAL ===== */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-white">
            Stay Connected
          </h3>

          <div className="flex gap-4 mb-6 text-xl">
            <a href="https://www.facebook.com/blogysports" target="_blank" rel="noreferrer">
              <FaFacebookF />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noreferrer">
              <FaTwitter />
            </a>
            <a href="https://instagram.com" target="_blank" rel="noreferrer">
              <FaInstagram />
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer">
              <FaLinkedinIn />
            </a>
            <a href="https://youtube.com" target="_blank" rel="noreferrer">
              <FaYoutube />
            </a>
          </div>

          <div className="flex">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="w-full px-3 py-2 rounded-l bg-[#1c1c1c] text-sm text-white outline-none"
            />
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="bg-orange-600 px-5 rounded-r text-white text-sm hover:bg-orange-700 disabled:opacity-60"
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            By subscribing, you agree to our Privacy Policy.
          </p>

          {message && (
            <p
              className={`text-sm mt-2 ${message.toLowerCase().includes("failed")
                ? "text-red-400"
                : "text-green-400"
                }`}
            >
              {message}
            </p>
          )}
        </div>
      </div>

      {/* ===== LEGAL BAR ===== */}
      <div className="border-t border-gray-800 py-5 text-sm">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-4">
          <p className="text-gray-500">
            © {new Date().getFullYear()} BlogySports. All rights reserved.
          </p>

          <div className="flex gap-6">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/privacy-policy" className="hover:text-white">Privacy</Link>
            <Link to="/terms-conditions" className="hover:text-white">Terms</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/disclaimer" className="hover:text-white">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
